import { SelectBox } from "components/uikit-adapter/index";
import type { ComponentType, ReactNode } from "react";
import { useEffect, useState } from "react";
import { Col, Row } from "react-flexbox-grid";
import intl from "react-intl-universal";
import {
  fetchDeliveryLocationOptions,
  fetchPickUpLocationOptions,
  type LocationOptionDto,
} from "services/suggestion/location-options.service";
import {
  buildDeliveryOptions,
  buildPickUpOptions,
  filterPickUpOptionsBySupplier,
  getConsigneeStockCandidates,
  getSelectedLocation,
  getSupplierOrigins,
  hasLegacyLocation,
  isSupplierOriginOption,
  type LocationId,
  type LocationOption,
  type LocationReadDto,
  type LocationRecord,
  normalizeLocationOptions,
  toLocationReadDto,
} from "./utils";

const PICK_UP_LOCATION = "PickUpLocation";
const DELIVERY_LOCATION = "DeliveryLocation";
const FlexRow = Row as unknown as ComponentType<{ children?: ReactNode }>;
const FlexCol = Col as unknown as ComponentType<{ children?: ReactNode; xs?: number }>;

type LocationFieldsProps = {
  item?: LocationRecord;
  supplier?: LocationRecord | null;
  supplierOrigins?: LocationRecord[];
  deliveryLocations?: LocationRecord[];
  destination?: LocationRecord | null;
  labeledFamilyCenterId?: LocationId | null;
  activeMaterialId?: LocationId | null;
  disabled?: boolean;
  alwaysShow?: boolean;
  onChange?: (name: string, value: LocationReadDto | null) => void;
};

const LocationFields = ({
  item = {},
  supplier = null,
  supplierOrigins = [],
  deliveryLocations = [],
  destination = null,
  labeledFamilyCenterId = null,
  activeMaterialId = null,
  disabled = false,
  alwaysShow = false,
  onChange = () => {},
}: LocationFieldsProps) => {
  const [apiOptions, setApiOptions] = useState<{
    pickUp: LocationOptionDto[];
    delivery: LocationOptionDto[];
  }>({ pickUp: [], delivery: [] });

  const [locationOptionsLoaded, setLocationOptionsLoaded] = useState(disabled);

  let supplierId: LocationId | undefined;
  if (typeof supplier?.Id === "string" || typeof supplier?.Id === "number") {
    supplierId = supplier.Id;
  } else if (
    typeof item.IdFornecedor === "string" ||
    typeof item.IdFornecedor === "number"
  ) {
    supplierId = item.IdFornecedor;
  }

  useEffect(() => {
    let isMounted = true;

    if (disabled) {
      setLocationOptionsLoaded(true);
      return () => {
        isMounted = false;
      };
    }

    setLocationOptionsLoaded(false);

    Promise.all([
      fetchPickUpLocationOptions({ supplierId }),
      fetchDeliveryLocationOptions({ labeledFamilyCenterId, materialId: activeMaterialId }),
    ])
      .then(([pickUp, delivery]) => {
        if (!isMounted) return;
        setApiOptions({ pickUp, delivery });
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error(error);
        setApiOptions({ pickUp: [], delivery: [] });
      })
      .finally(() => {
        if (isMounted) setLocationOptionsLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, [activeMaterialId, disabled, labeledFamilyCenterId, supplierId]);

  const persistedPickUpLocation = getSelectedLocation(item, PICK_UP_LOCATION, []);
  const persistedDeliveryLocation = getSelectedLocation(item, DELIVERY_LOCATION, []);
  const originsFromSupplier = getSupplierOrigins(item, supplier);
  const origins = supplierOrigins.length > 0 ? supplierOrigins : originsFromSupplier;
  const localPickUpOptions = buildPickUpOptions(supplier, origins);
  const localDeliveryOptions = buildDeliveryOptions(
    destination,
    getConsigneeStockCandidates(item, deliveryLocations)
  );
  const pickUpOptions = locationOptionsLoaded
    ? normalizeLocationOptions(filterPickUpOptionsBySupplier(apiOptions.pickUp, supplier))
    : localPickUpOptions;
  const deliveryOptions = locationOptionsLoaded
    ? normalizeLocationOptions(apiOptions.delivery)
    : localDeliveryOptions;
  const selectedPickUpLocation = getSelectedLocation(item, PICK_UP_LOCATION, pickUpOptions);
  const selectedDeliveryLocation = getSelectedLocation(item, DELIVERY_LOCATION, deliveryOptions);
  const hasPersistedLocation = Boolean(persistedPickUpLocation || persistedDeliveryLocation);
  const hasSupplierOrigins = pickUpOptions.some(isSupplierOriginOption);
  const showFields = hasPersistedLocation || (locationOptionsLoaded && hasSupplierOrigins);

  if (showFields && !alwaysShow) return null;

  const hasLegacyValue =
    hasLegacyLocation(item, PICK_UP_LOCATION, pickUpOptions) ||
    hasLegacyLocation(item, DELIVERY_LOCATION, deliveryOptions);
  const readOnly = disabled || hasLegacyValue;
  const visiblePickUpOptions =
    readOnly && selectedPickUpLocation ? [selectedPickUpLocation] : pickUpOptions;
  const visibleDeliveryOptions =
    readOnly && selectedDeliveryLocation ? [selectedDeliveryLocation] : deliveryOptions;

  return (
    <FlexRow>
      <FlexCol xs={6}>
        <SelectBox
          name="PickUpLocation"
          label={intl.get("stocks.suggestionsListItem.pickUpLocation")}
          placeholder={intl.get("stocks.suggestionsListItem.pickUpLocation")}
          value={selectedPickUpLocation}
          options={visiblePickUpOptions}
          valueKey="value"
          labelKey="label"
          onChange={(value: LocationOption | null) =>
            onChange(PICK_UP_LOCATION, toLocationReadDto(value))
          }
          clearable={!readOnly}
          searchable={!readOnly}
          openOnFocus={!readOnly}
          disabled={readOnly}
          data-testid="pick-up-location"
        />
      </FlexCol>
      <FlexCol xs={6}>
        <SelectBox
          name="DeliveryLocation"
          label={intl.get("stocks.suggestionsListItem.deliveryLocation")}
          placeholder={intl.get("stocks.suggestionsListItem.deliveryLocation")}
          value={selectedDeliveryLocation}
          options={visibleDeliveryOptions}
          valueKey="value"
          labelKey="label"
          onChange={(value: LocationOption | null) =>
            onChange(DELIVERY_LOCATION, toLocationReadDto(value))
          }
          clearable={!readOnly}
          searchable={!readOnly}
          openOnFocus={!readOnly}
          disabled={readOnly}
          data-testid="delivery-location"
        />
      </FlexCol>
    </FlexRow>
  );
};

export default LocationFields;
